/* eslint-disable max-lines */
/* eslint-disable react/prop-types */
import { useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import H4 from '@base/H4/H4';
import H6 from '@base/H6/H6';
import Menu from '@base/Menu/Menu';
import Link from '@base/Link/Link';
import Drawer from '@base/Drawer/Drawer';
import ChevronRight from '@icon/ChevronRight';
import LearnOnTheGo from '@comp/LearnOnTheGo/LearnOnTheGo';
import CustomModal from '@comp/CustomModal/CustomModal';
import { useSelectorWithShallowEqual } from '@hooks/reduxHooks';
import cookies from '@utils/cookies';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import { setSelectedNav } from '@actions/auth';
import pushRoute from '@utils/pushRoute';
import Dynamic from '@base/Dynamic/Dynamic';
import { Spacings } from '@styles/index';
import {
  PRACTICE_SECTION_CLICKED,
  SYLLABUS_TAB_CLICKED,
  GOAL_KEYS
} from '@constants/segment';
import { evtNameCategoryMap } from '@constants/index';
import analyticsAction, { segment } from '@actions/analytics';

import BaseFooter from '../Footer/BaseFooter/Footer';

const ReportIssue = Dynamic(() => import('./ReportIssue'));

const PlusNavBarWrapper = styled.div`
  position: relative;
  grid-column: 1/6;
  z-index: 2;
`;

const PlusNavBarFixed = styled.div`
  position: sticky;
  width: 225px;
  top: ${Spacings.SPACING_HEADER + Spacings.SPACING_HEADER_MARGIN}px;
`;

const Selected = styled.div`
  height: ${({ selectedElHeight }) => selectedElHeight}px;
  width: 100%;
  position: absolute;
  background-color: ${({ theme }) => theme.colors.white};
  top: ${({ selectedElPos, selectedElHeight }) =>
    selectedElPos + (selectedElHeight > 40 ? 10 : 5)}px;
  transition: all 400ms ease-in;
  transition-property: top, height;
  border-radius: 8px;
  box-shadow: ${({ theme }) =>
    boxShadowGenerator({
      yOffset: Spacings.SPACING_1B,
      blur: Spacings.SPACING_4B,
      theme
    })};
`;

const NavItems = styled.div`
  position: relative;
  overflow: hidden;
  width: 100%;
  /* & > :not(:first-child) {
    margin-top: 36px;
  } */
  & > :last-child {
    margin-top: 140px;
  }
`;

const MainItem = styled(H4)`
  padding: 8px 16px;
  transition: color 400ms ease-in-out;
  color: ${({ selected, theme }) =>
    selected ? theme.colors.green : theme.colors.fontGrey};
  :hover {
    color: ${({ disabled, theme }) =>
      disabled ? 'inherit' : theme.colors.green};
  }
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
`;

const Item = styled(H6)`
  padding: 8px 16px;
  transition: color 400ms ease-in-out;
  color: ${({ selected, theme }) =>
    selected ? theme.colors.green : theme.colors.fontLightGrey};
  font-weight: ${({ selected }) => (selected ? 600 : 'normal')};
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  :hover {
    color: ${({ disabled, theme }) =>
      disabled ? theme.colors.fontLightGrey : theme.colors.green};
  }
`;

const MenuContainer = styled.div`
  margin-bottom: 24px;
`;

const Others = styled.div`
  position: fixed;
  bottom: ${Spacings.SPACING_8B};
  width: 225px;
`;

const LinkContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const StyledChevronRight = styled(ChevronRight)`
  margin-right: ${Spacings.SPACING_4B};
`;

const PlusNavBar = ({ display }) => {
  const router = useRouter();
  const selectedGoal = useSelectorWithShallowEqual(
    ({ auth }) => auth.selectedGoal ?? {}
  );
  const { goalUID: routerGoalUID } = router.query;
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedGoalUid, setSelectedGoalUid] = useState(
    cookies.readCookie('selectedGoalUid')
  );
  const [openReportIssue, setOpenReportIssue] = useState(false);
  const closeDrawer = () => setOpenReportIssue(false);
  const openDrawer = () => setOpenReportIssue(true);
  const [openOtherLinks, setOpenOtherLinks] = useState(false);
  const closeOtherLinksModal = () => setOpenOtherLinks(false);
  const openOtherLinksModal = () => setOpenOtherLinks(true);
  const [openLearnOnTheGo, setOpenLearnOnTheGo] = useState(false);
  const closeLearnOnTheGo = () => setOpenLearnOnTheGo(false);
  const openLearnOnTheGoModal = () => setOpenLearnOnTheGo(true);
  const selectedNav = useSelector(({ auth }) => auth.selectedNav);
  const isLoggedIn = useSelector(({ auth }) => auth.isLoggedIn);
  const me = useSelector(({ auth }) => auth.me, shallowEqual);
  const plusSubscriptions = useSelector(
    ({ auth }) => auth.me.plusSubscriptionUids,
    shallowEqual
  );
  const cookieGoalInfo = useSelector(
    ({ topologies }) => topologies.data[selectedGoalUid] ?? {},
    shallowEqual
  );
  const goalInfo = useSelector(
    ({ auth, topologies }) => topologies.data[auth.selectedGoal?.uid] ?? {},
    shallowEqual
  );

  const routerGoalInfo = useSelector(
    ({ topologies }) => topologies.data[routerGoalUID] ?? {},
    shallowEqual
  );
  const isChatbotEnabled = me?.plusSubscriptionUids?.length > 0;

  useEffect(() => {
    setSelectedGoalUid(cookies.readCookie('selectedGoalUid'));
  }, [isLoggedIn]);

  const [selectedElHeight, setselectedElHeight] = useState(0);
  const [selectedElPos, setselectedElPos] = useState(0);
  const dispatch = useDispatch();

  const sendSegmentEvent = (nav) => {
    let eventName = null;
    switch (nav) {
      case 'topics':
        eventName = SYLLABUS_TAB_CLICKED;
        break;
      default:
        eventName = null;
    }
    if (eventName)
      segment.track(
        eventName,
        {},
        evtNameCategoryMap.WITHOUT_CATEGORY,
        true,
        GOAL_KEYS
      );
  };

  const handleClick = (nav, disabled) => () => {
    if (disabled) return;
    dispatch(setSelectedNav(nav));
    dispatch(analyticsAction(`plusnav-${nav}`));
    sendSegmentEvent(nav);
  };

  const openHelpcenter = () => {
    pushRoute('/helpcenter', '/helpcenter');
  };

  const onMenuClick = (e) => {
    setAnchorEl(e.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handlePracticeNavClicked = () => {
    segment.track(PRACTICE_SECTION_CLICKED, {});
    handleMenuClose();
  };

  useEffect(() => {
    const el = document.querySelector(`[data-id="${selectedNav}"]`);
    const selectedElemHeight = el?.offsetHeight;
    const selectedElemOffset = el?.offsetTop;
    setselectedElHeight(selectedElemHeight);
    setselectedElPos(selectedElemOffset - (selectedElemHeight > 40 ? 10 : 6));
  }, [selectedNav]);

  if (!display) return null;
  let goalAs = null;
  const goalHref = '/goal/[goalSlug]/[goalUID]';
  if (selectedGoal?.uid) {
    goalAs = `/goal/${selectedGoal?.slug || selectedGoal?.goalSlug}/${
      selectedGoal?.uid
    }`;
  } else if (routerGoalUID) {
    goalAs = `/goal/${
      routerGoalInfo?.goalSlug || routerGoalInfo?.slug
    }/${routerGoalUID}`;
  }

  let isDisabled = true;
  let publicLinksDisabled = true;

  if (plusSubscriptions?.includes(goalInfo?.uid) && goalInfo?.isPlusAvailable) {
    isDisabled = false;
  }

  if (
    router.route.includes('/library') &&
    selectedGoalUid &&
    plusSubscriptions?.includes(selectedGoalUid.toUpperCase()) &&
    cookieGoalInfo?.isPlusAvailable
  ) {
    isDisabled = false;
  }

  if (
    (router.query.goalUID && goalInfo?.isPlusAvailable) ||
    (router.route.includes('/library') &&
      selectedGoalUid &&
      cookieGoalInfo?.isPlusAvailable) ||
    (router.route.includes('/search/') && goalInfo?.isPlusAvailable)
  ) {
    publicLinksDisabled = false;
  }

  const isTestSeriesURL =
    router.route === '/goal/[goalSlug]/[goalUID]/test-series';
  const isPracticeURL = router.route === '/goal/[goalSlug]/[goalUID]/practice';

  return (
    <PlusNavBarWrapper>
      <PlusNavBarFixed>
        <Selected
          selectedElPos={selectedElPos}
          selectedElHeight={selectedElHeight}
        />
        <Drawer drawerState={openReportIssue} onClose={closeDrawer}>
          {openReportIssue && <ReportIssue closeDrawer={closeDrawer} />}
        </Drawer>
        <LearnOnTheGo
          onClose={closeLearnOnTheGo}
          modalState={openLearnOnTheGo}
          isFloating
        />
        <CustomModal
          overlayTransparent
          modalState={openOtherLinks}
          onClose={closeOtherLinksModal}
          bottomFrom="20px"
          bottomTo="40px"
          gridColumn="6/20"
        >
          <BaseFooter display floating />
        </CustomModal>
        <NavItems>
          <MenuContainer>
            <Link
              disabled={!goalInfo?.isPlusAvailable}
              href={goalHref}
              as={goalAs}
              onClick={handleClick('plus', !goalInfo?.isPlusAvailable)}
            >
              <MainItem
                disabled={!goalInfo?.isPlusAvailable}
                selected={selectedNav === 'plus'}
                data-id="plus"
              >
                Home
              </MainItem>
            </Link>
            <Link
              disabled={isDisabled}
              href={`${goalHref}/schedule`}
              as={`${goalAs}/schedule`}
              onClick={handleClick('schedule', isDisabled)}
            >
              <Item
                disabled={isDisabled}
                selected={selectedNav === 'schedule'}
                data-id="schedule"
              >
                Schedule
              </Item>
            </Link>
            <Link
              disabled={isDisabled}
              href={`${goalHref}/saved`}
              as={`${goalAs}/saved`}
              onClick={handleClick('saved', isDisabled)}
            >
              <Item
                disabled={isDisabled}
                selected={selectedNav === 'saved'}
                data-id="saved"
              >
                Saved
              </Item>
            </Link>
            <Link
              disabled={isDisabled}
              href={`${goalHref}/enrolled`}
              as={`${goalAs}/enrolled`}
              onClick={handleClick('enrolled', isDisabled)}
            >
              <Item
                disabled={isDisabled}
                selected={selectedNav === 'enrolled'}
                data-id="enrolled"
              >
                Enrollments
              </Item>
            </Link>
            <Link
              disabled={publicLinksDisabled}
              href={`${goalHref}/topics`}
              as={`${goalAs}/topics`}
              onClick={handleClick('topics', publicLinksDisabled)}
            >
              <Item
                disabled={publicLinksDisabled}
                selected={selectedNav === 'topics'}
                data-id="topics"
              >
                {goalInfo?.isNoTestGoal ? 'Key concepts' : 'Syllabus'}
              </Item>
            </Link>
            {!goalInfo.isNoTestGoal && !goalInfo.isPracticeAvailable && (
              <Link
                disabled={publicLinksDisabled}
                href={`${goalHref}/test-series`}
                as={`${goalAs}/test-series`}
                onClick={handleClick(
                  'test-series-practice',
                  publicLinksDisabled
                )}
              >
                <Item
                  disabled={publicLinksDisabled}
                  selected={selectedNav === 'test-series-practice'}
                  data-id="test-series-practice"
                >
                  Test series
                </Item>
              </Link>
            )}
            {!goalInfo.isNoTestGoal && goalInfo.isPracticeAvailable && (
              <LinkContainer onClick={onMenuClick}>
                <Item
                  data-id="test-series-practice"
                  selected={selectedNav === 'test-series-practice'}
                >
                  Test series & Practice
                </Item>
                <StyledChevronRight size="16px" color="#808080" />
              </LinkContainer>
            )}
          </MenuContainer>
          <Menu
            anchorEl={anchorEl}
            onClose={handleMenuClose}
            anchorOrigin={{
              vertical: -10,
              horizontal: 250
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left'
            }}
            width="172px"
          >
            {!goalInfo.isNoTestGoal && (
              <Link
                disabled={publicLinksDisabled}
                href={`${goalHref}/test-series`}
                as={`${goalAs}/test-series`}
                onClick={handleClick(
                  'test-series-practice',
                  publicLinksDisabled
                )}
              >
                <Item
                  disabled={publicLinksDisabled}
                  selected={isTestSeriesURL}
                  onClick={handleMenuClose}
                >
                  Test series
                </Item>
              </Link>
            )}
            {goalInfo.isPracticeAvailable && (
              <Link
                disabled={isDisabled}
                href={`${goalHref}/practice`}
                as={`${goalAs}/practice`}
                onClick={handleClick('test-series-practice', isDisabled)}
              >
                <Item
                  disabled={isDisabled}
                  selected={isPracticeURL}
                  onClick={handlePracticeNavClicked}
                >
                  Practice
                </Item>
              </Link>
            )}
          </Menu>
          <MenuContainer>
            <Link
              href={`${goalHref}/classes`}
              as={`${goalAs}/classes`}
              onClick={handleClick('classes')}
            >
              <MainItem selected={selectedNav === 'classes'} data-id="classes">
                {goalInfo.isNoTestGoal
                  ? 'Free classes'
                  : 'Free classes & tests'}
              </MainItem>
            </Link>
          </MenuContainer>
          {!goalInfo.isFreeUnavailable && (
            <MenuContainer>
              <Link
                href={`${goalHref}/free-platform`}
                as={`${goalAs}/free-platform`}
                onClick={handleClick('free-platform')}
              >
                <MainItem
                  selected={selectedNav === 'free-platform'}
                  data-id="free-platform"
                >
                  Quick courses
                </MainItem>
              </Link>
              <Link
                disabled={!isLoggedIn}
                href="/library"
                onClick={handleClick('library', !isLoggedIn)}
              >
                <Item
                  disabled={!isLoggedIn}
                  selected={selectedNav === 'library'}
                  data-id="library"
                >
                  My library
                </Item>
              </Link>
            </MenuContainer>
          )}
          <Others>
            <>
              {isChatbotEnabled ? (
                <LinkContainer>
                  <Item onClick={openHelpcenter}>Help & Support</Item>
                  <StyledChevronRight size="16px" color="#808080" />
                </LinkContainer>
              ) : (
                <LinkContainer>
                  <Item onClick={openDrawer}>Report an issue</Item>
                  <StyledChevronRight size="16px" color="#808080" />
                </LinkContainer>
              )}
              <LinkContainer>
                <Item
                  data-id="myLibrary"
                  selected={openLearnOnTheGo}
                  onClick={openLearnOnTheGoModal}
                >
                  Get the learning app
                </Item>
                <StyledChevronRight size="16px" color="#808080" />
              </LinkContainer>
              <LinkContainer>
                <Item selected={openOtherLinks} onClick={openOtherLinksModal}>
                  Other links
                </Item>
                <StyledChevronRight size="16px" color="#808080" />
              </LinkContainer>
            </>
          </Others>
        </NavItems>
      </PlusNavBarFixed>
    </PlusNavBarWrapper>
  );
};

export default PlusNavBar;

PlusNavBar.propTypes = {
  display: PropTypes.bool
};

PlusNavBar.defaultProps = {
  display: false
};
